import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type Ref,
  type RefObject,
} from 'react'
import {XIcon} from '@primer/octicons-react'
import {Button, CounterLabel, IconButton, SegmentedControl} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {Tooltip} from '@primer/react/next'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {
  DockedPanel,
  useDockedPanelActions,
  useDockedPanelFocusRefs,
  useIsDockedPanelCompact,
  useIsDockedPanelOpen,
} from '../../contexts/DockedPanelContext'
import {
  useBatchComposition,
  useIsFixWithCopilotAvailable,
  useUnifiedBatchActions,
  useUnifiedBatchCount,
  type BatchComposition,
} from '../../stores/layout-selectors'
import {Panel} from './Panel'
import {CompactPanel} from './CompactPanel'
import {getPanelHeading} from './panel-registry'
import {restoreFocusOnNextFrame, restorePanelFocusAfterClose} from './panel-focus'
import {MANAGE_BATCH_COMMENTS_HEADING_ID, ManageBatchCommentsList} from './manage-batch/ManageBatchCommentsList'
import {BatchCommentCollapseProvider} from './manage-batch/use-batch-comment-collapse'
import {ToggleAllBatchedCommentsButton} from './manage-batch/ToggleAllBatchedCommentsButton'
import {
  ManageBatchCommitFormCommands,
  ManageBatchCommitProvider,
  ManageBatchCommitTab,
  ManageBatchCommitTabPrimaryCta,
} from './manage-batch/ManageBatchCommitTab'
import {ManageBatchDisallowedTabBanner} from './manage-batch/ManageBatchDisallowedTabBanner'
import {
  ManageBatchFixFormCommands,
  ManageBatchFixProvider,
  ManageBatchFixTab,
  ManageBatchFixTabPrimaryCta,
} from './manage-batch/ManageBatchFixTab'
import {useManageBatchTelemetry} from './manage-batch/manage-batch-telemetry'
import styles from './ManageBatchPanel.module.css'

export type ManageBatchTab = 'commit' | 'fix'

const TAB_DEFS: Array<{value: ManageBatchTab; label: string}> = [
  {value: 'commit', label: 'Commit suggestions'},
  {value: 'fix', label: 'Fix with Copilot'},
]

const TAB_VALUES = TAB_DEFS.map(({value}) => value)
const FIX_WITH_COPILOT_UNAVAILABLE_MESSAGE = 'Fix with Copilot isn’t available.'
const COMMIT_NO_SUGGESTIONS_MESSAGE = 'This batch does not contain any suggestions that can be committed.'
const COMMIT_MIXED_MESSAGE = 'This batch contains items other than committable suggestions, so it can’t be committed.'

/**
 * Copy for the inactive Commit segment's explanation, keyed on batch composition.
 * A `mixed` batch has committable suggestions plus items that can't be committed
 * (comments or outdated suggestions), so it gets its own wording; the other
 * non-committable compositions (`no-suggestion`, `empty`) share the
 * "no suggestions to commit" message. (`suggestion-only` never reaches here
 * because Commit stays active for it.)
 */
function getCommitDisallowedMessage(composition: BatchComposition): string {
  return composition === 'mixed' ? COMMIT_MIXED_MESSAGE : COMMIT_NO_SUGGESTIONS_MESSAGE
}

/**
 * Manage batch panel shell.
 *
 * Hosts the docked-panel surface for the unified batch experience. This file
 * delivers the structural pieces only:
 *  - Tab header (Commit suggestions | Fix with Copilot).
 *  - Shared `Comments(N)` list with per-row remove.
 *  - Footer slot framework (Discard batch / Cancel / per-tab primary CTA slot).
 *  - Empty state when the batch is empty.
 *
 * The tab-body content slots are wired by the tab-content follow-ups:
 *  - Commit suggestions tab content
 *  - Fix with Copilot tab content
 *  - Outdated suggestion handling
 *
 * When the batch has no committable suggestions the Commit segment is rendered
 * inactive (Primer `aria-disabled`, still focusable) with a tooltip explaining
 * why, and the panel defaults to — and auto-switches to — Fix with Copilot
 * whenever Fix is available.
 *
 * Gated behind `unified_batch_pr_comments`. Registered in `panel-registry.tsx`
 * so it joins the enabled-panel set (and the panel-selector dropdown) when
 * the flag is on. Mounted by `DockedSidePanelContent` via `panel-render-map`
 * when the panel is active and the viewport is non-compact; falls back to a
 * Dialog in compact mode.
 */
export const ManageBatchPanel = memo(function ManageBatchPanelImpl() {
  const isFlagEnabled = isFeatureEnabled('unified_batch_pr_comments')
  const updatedPullRequestHeader = isFeatureEnabled('updated_pull_request_header')
  const isDockedPanelOpen = useIsDockedPanelOpen(DockedPanel.ManageBatchReviewComments)
  const isCompact = useIsDockedPanelCompact()
  const {clearBatch} = useUnifiedBatchActions()
  const batchComposition = useBatchComposition()
  const isFixWithCopilotAvailable = useIsFixWithCopilotAvailable()
  const {returnFocusRef} = useDockedPanelFocusRefs()
  const closeManageBatchPanel = useCloseManageBatchPanel()
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const tabsHeaderRef = useRef<HTMLDivElement>(null)
  const {onTabSelected} = useManageBatchTelemetry()
  const commitDisallowed = batchComposition !== 'suggestion-only'
  const shouldDefaultToCommit = !isFixWithCopilotAvailable || batchComposition === 'suggestion-only'
  const [activeTab, setActiveTab] = useState<ManageBatchTab>(() => (shouldDefaultToCommit ? 'commit' : 'fix'))

  // If the batch loses its last committable suggestion while Commit is active,
  // the Commit segment goes inactive under the user. Quietly move them to Fix
  // with Copilot — but only when Fix is available, so we never land on a disabled
  // tab. Detect the edge via set-state-during-render (not an effect) so it can't
  // trigger cascading renders; the guard on `activeTab === 'commit'` makes the
  // correction self-terminating. A counter defers the telemetry + focus side
  // effects to an effect so they each fire exactly once per switch.
  const [autoSwitchCount, setAutoSwitchCount] = useState(0)
  const autoSwitchReportedRef = useRef(0)
  if (isFlagEnabled && isDockedPanelOpen && activeTab === 'commit' && commitDisallowed && isFixWithCopilotAvailable) {
    setActiveTab('fix')
    setAutoSwitchCount(count => count + 1)
  }

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- The switch originates from batch mutations outside this component; report the observed transition and repair focus.
    if (autoSwitchCount === autoSwitchReportedRef.current) return
    autoSwitchReportedRef.current = autoSwitchCount

    onTabSelected('fix', 'auto_switch')

    // Restore focus to the Fix tab only when the auto-switch orphaned it (the
    // focused element in the now-unmounted Commit body dropped focus to <body>).
    // If the user is focused elsewhere on purpose, leave them alone.
    const activeElement = document.activeElement
    if (activeElement === null || activeElement === document.body) {
      restoreFocusOnNextFrame(tabsHeaderRef.current?.querySelector<HTMLElement>('#manage-batch-tab-fix'))
    }
  }, [autoSwitchCount, onTabSelected])

  // Success handler used by the Commit tab: clear the batch and close the panel.
  // (The Commit mutation also clears + reloads first so the committed diff shows;
  // the clear here is idempotent.) The Fix with Copilot tab does NOT use this — it
  // clears the batch in its own mutation's `onSuccess` and deliberately keeps the
  // panel open so the returned `taskUrl` can surface the "View agent session" CTA.
  const handleSubmitted = useCallback(() => {
    clearBatch()
    closeManageBatchPanel()
  }, [clearBatch, closeManageBatchPanel])

  if (!isFlagEnabled || !isDockedPanelOpen) return null

  if (!isCompact) {
    return (
      <ActiveTabProvider onSubmitted={handleSubmitted} activeTab={activeTab}>
        <Panel panelName={DockedPanel.ManageBatchReviewComments}>
          <Panel.Header>
            <ManageBatchTabsHeader
              activeTab={activeTab}
              isFixWithCopilotAvailable={isFixWithCopilotAvailable}
              setActiveTab={setActiveTab}
              tabsHeaderRef={tabsHeaderRef}
            />
          </Panel.Header>
          <Panel.Body scrollable={false} className={styles.bodyLayout}>
            <ManageBatchBody activeTab={activeTab} isFixWithCopilotAvailable={isFixWithCopilotAvailable} />
          </Panel.Body>
        </Panel>
      </ActiveTabProvider>
    )
  }

  if (!updatedPullRequestHeader) {
    return (
      <ActiveTabProvider onSubmitted={handleSubmitted} activeTab={activeTab}>
        <Dialog
          initialFocusRef={closeButtonRef}
          position={{narrow: 'fullscreen', regular: 'right', wide: 'right'}}
          returnFocusRef={returnFocusRef}
          onClose={closeManageBatchPanel}
          renderHeader={({dialogLabelId}) => (
            <Dialog.Header>
              <div className="d-flex flex-row flex-justify-between flex-items-center width-full pl-2">
                <Dialog.Title id={dialogLabelId}>{getPanelHeading(DockedPanel.ManageBatchReviewComments)}</Dialog.Title>
                <IconButton
                  ref={closeButtonRef}
                  aria-label="Close manage batch panel"
                  icon={XIcon}
                  variant="invisible"
                  onClick={closeManageBatchPanel}
                />
              </div>
              <ManageBatchTabsHeader
                activeTab={activeTab}
                isFixWithCopilotAvailable={isFixWithCopilotAvailable}
                setActiveTab={setActiveTab}
                tabsHeaderRef={tabsHeaderRef}
              />
            </Dialog.Header>
          )}
        >
          <div className={`flex-1 overflow-hidden ${styles.bodyLayout}`}>
            <ManageBatchBody activeTab={activeTab} isFixWithCopilotAvailable={isFixWithCopilotAvailable} />
          </div>
        </Dialog>
      </ActiveTabProvider>
    )
  }

  return (
    <ActiveTabProvider onSubmitted={handleSubmitted} activeTab={activeTab}>
      <CompactPanel.Header>
        <ManageBatchTabsHeader
          activeTab={activeTab}
          isFixWithCopilotAvailable={isFixWithCopilotAvailable}
          setActiveTab={setActiveTab}
          tabsHeaderRef={tabsHeaderRef}
        />
      </CompactPanel.Header>
      <CompactPanel.Content>
        <ManageBatchBody activeTab={activeTab} isFixWithCopilotAvailable={isFixWithCopilotAvailable} />
      </CompactPanel.Content>
    </ActiveTabProvider>
  )
})

/**
 * Per-tab context wrapper. Each tab's body and primary CTA render in separate
 * parts of the panel's top section — the tab body and the actions row — so
 * they can't share component-local state. We wrap the entire panel in both
 * tabs' providers so each tab can read and update its own form state and
 * submit callback. Both are mounted unconditionally (rather than only while
 * their own tab is active) so the user's edited commit message/description and
 * the Fix tab's state survive switching tabs and back. Each tab consumes only
 * its own context, so keeping both mounted is harmless.
 */
function ActiveTabProvider({
  onSubmitted,
  children,
  activeTab,
}: {
  onSubmitted: () => void
  children: React.ReactNode
  activeTab: ManageBatchTab
}) {
  return (
    <ManageBatchCommitProvider onSubmitted={onSubmitted} generationActive={activeTab === 'commit'}>
      <ManageBatchFixProvider onSubmitted={onSubmitted}>{children}</ManageBatchFixProvider>
    </ManageBatchCommitProvider>
  )
}

function ManageBatchTabsHeader({
  activeTab,
  isFixWithCopilotAvailable,
  setActiveTab,
  tabsHeaderRef,
}: {
  activeTab: ManageBatchTab
  isFixWithCopilotAvailable: boolean
  setActiveTab: (tab: ManageBatchTab) => void
  tabsHeaderRef: RefObject<HTMLDivElement | null>
}) {
  const {onTabSelected} = useManageBatchTelemetry()
  const batchComposition = useBatchComposition()
  const commitDisallowed = batchComposition !== 'suggestion-only'

  const handleChange = useCallback(
    (selectedIndex: number) => {
      const next = TAB_VALUES[selectedIndex] ?? 'commit'
      // Defensive: SegmentedControl already suppresses selection of an inactive
      // (aria-disabled) segment, but guard here too so an inactive tab can never
      // flip the active tab or emit a tab_selected event.
      if (next === 'fix' && !isFixWithCopilotAvailable) return
      if (next === 'commit' && commitDisallowed) return

      setActiveTab(next)
      onTabSelected(next)
    },
    [isFixWithCopilotAvailable, commitDisallowed, setActiveTab, onTabSelected],
  )

  // A single Primer Tooltip wraps the whole control — nesting a Tooltip around an
  // individual segment would break SegmentedControl's click guard and produce
  // invalid list markup. Its anchor + copy are computed dynamically: an
  // unavailable Fix segment takes priority (preserving the explicitly-denied-user
  // experience), otherwise an inactive Commit segment explains why commit isn't
  // possible.
  const tooltipAnchor: ManageBatchTab | null = !isFixWithCopilotAvailable ? 'fix' : commitDisallowed ? 'commit' : null
  const tooltipText =
    tooltipAnchor === 'fix' ? FIX_WITH_COPILOT_UNAVAILABLE_MESSAGE : getCommitDisallowedMessage(batchComposition)

  return (
    <div ref={tabsHeaderRef}>
      <Tooltip text={tooltipText} type="description" direction="n">
        <ManageBatchSegmentedControl
          activeTab={activeTab}
          isFixWithCopilotAvailable={isFixWithCopilotAvailable}
          commitDisallowed={commitDisallowed}
          tooltipAnchor={tooltipAnchor}
          onChange={handleChange}
        />
      </Tooltip>
    </div>
  )
}

type ManageBatchSegmentedControlProps = {
  activeTab: ManageBatchTab
  isFixWithCopilotAvailable: boolean
  commitDisallowed: boolean
  tooltipAnchor: ManageBatchTab | null
  onChange: (selectedIndex: number) => void
  ref?: Ref<HTMLButtonElement>
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'>

function ManageBatchSegmentedControl({
  activeTab,
  isFixWithCopilotAvailable,
  commitDisallowed,
  tooltipAnchor,
  onChange,
  ref: forwardedRef,
  ...tooltipTriggerProps
}: ManageBatchSegmentedControlProps) {
  return (
    <SegmentedControl aria-label="Manage batch action mode" fullWidth onChange={onChange} className={styles.tabs}>
      {TAB_DEFS.map(({value, label}) => {
        // Primer's `disabled` maps to `aria-disabled`, so the segment stays
        // focusable but its selection is guarded — Primer's "inactive" behavior.
        const disabled = value === 'fix' ? !isFixWithCopilotAvailable : commitDisallowed
        // The Tooltip clones this control and injects its trigger handlers + ref;
        // forward them to whichever segment is the current tooltip anchor so the
        // styled tooltip attaches to the right button and its `aria-describedby`
        // lands there. Exactly one segment is the anchor at a time.
        const isTooltipAnchor = value === tooltipAnchor

        return (
          <SegmentedControl.Button
            key={value}
            {...(isTooltipAnchor ? {ref: forwardedRef, ...tooltipTriggerProps} : {})}
            id={`manage-batch-tab-${value}`}
            selected={activeTab === value}
            disabled={disabled}
            data-testid={`manage-batch-tab-${value}`}
          >
            {label}
          </SegmentedControl.Button>
        )
      })}
    </SegmentedControl>
  )
}

function ManageBatchBody({
  activeTab,
  isFixWithCopilotAvailable,
}: {
  activeTab: ManageBatchTab
  isFixWithCopilotAvailable: boolean
}) {
  const count = useUnifiedBatchCount()

  // The top section holds the active tab's form fields and the actions row
  // (Discard / Cancel / primary CTA). For each tab we wrap it in the tab's
  // `*FormCommands` so ⌘/Ctrl+Enter submits the batch from anywhere in the
  // form area — mirroring `ApplySuggestionDialog`.
  const topSection = (
    <div className={styles.panelTop}>
      <div data-testid={`manage-batch-tab-body-${activeTab}`}>
        {activeTab === 'commit' && (
          <>
            <ManageBatchDisallowedTabBanner />
            <ManageBatchCommitTab />
          </>
        )}
        {activeTab === 'fix' && isFixWithCopilotAvailable && <ManageBatchFixTab />}
      </div>
      <ManageBatchActions activeTab={activeTab} />
    </div>
  )

  return (
    <>
      {activeTab === 'commit' && <ManageBatchCommitFormCommands>{topSection}</ManageBatchCommitFormCommands>}
      {activeTab === 'fix' &&
        (isFixWithCopilotAvailable ? (
          <ManageBatchFixFormCommands>{topSection}</ManageBatchFixFormCommands>
        ) : (
          topSection
        ))}
      <BatchCommentCollapseProvider>
        <div className={styles.panelScrollable}>
          <div className={styles.commentsHeadingRow}>
            <h3 className={styles.commentsHeading} id={MANAGE_BATCH_COMMENTS_HEADING_ID} tabIndex={-1}>
              Batched comments <CounterLabel className="ml-1">{count}</CounterLabel>
            </h3>
            <ToggleAllBatchedCommentsButton />
          </div>
          <ManageBatchCommentsList />
        </div>
      </BatchCommentCollapseProvider>
    </>
  )
}

function ManageBatchActions({activeTab}: {activeTab: ManageBatchTab}) {
  const isFixWithCopilotAvailable = useIsFixWithCopilotAvailable()
  const closeManageBatchPanel = useCloseManageBatchPanel()
  const {clearBatch} = useUnifiedBatchActions()
  const {onPanelClose} = useManageBatchTelemetry()

  const handleDiscard = useCallback(() => {
    onPanelClose('discard')
    closeManageBatchPanel()
    clearBatch()
  }, [clearBatch, closeManageBatchPanel, onPanelClose])

  const handleCancel = useCallback(() => {
    onPanelClose('cancel')
    closeManageBatchPanel()
  }, [closeManageBatchPanel, onPanelClose])

  return (
    <div className={styles.actions}>
      <Button variant="danger" onClick={handleDiscard}>
        Discard batch
      </Button>
      <div className={styles.actionsRight}>
        <Button variant="default" onClick={handleCancel}>
          Cancel
        </Button>
        <div data-testid={`manage-batch-primary-cta-slot-${activeTab}`}>
          {activeTab === 'commit' && <ManageBatchCommitTabPrimaryCta />}
          {activeTab === 'fix' && isFixWithCopilotAvailable && <ManageBatchFixTabPrimaryCta />}
        </div>
      </div>
    </div>
  )
}

function useCloseManageBatchPanel() {
  const {closePanel} = useDockedPanelActions()
  const {returnFocusRef} = useDockedPanelFocusRefs()

  return useCallback(() => {
    restorePanelFocusAfterClose(returnFocusRef.current)
    closePanel()
  }, [closePanel, returnFocusRef])
}
